import { X } from "lucide-react";
import { useTranslate } from "ra-core";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { PapelGuia, PasoGuia } from "./pasos";

type Caja = { top: number; left: number; width: number; height: number };

const MARGEN = 16;
const HOLGURA = 6;
const ANCHO_TARJETA = 360;

/** El primer elemento visible que coincide con el selector (escritorio o móvil). */
const buscar = (selector: string): HTMLElement | null => {
  for (const el of Array.from(
    document.querySelectorAll<HTMLElement>(selector),
  )) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return el;
  }
  return null;
};

/** La caja del elemento recortada a la pantalla, con un poco de aire. */
const cajaDe = (el: HTMLElement): Caja => {
  const r = el.getBoundingClientRect();
  const top = Math.max(r.top - HOLGURA, 4);
  const left = Math.max(r.left - HOLGURA, 4);
  const bottom = Math.min(r.bottom + HOLGURA, window.innerHeight - 4);
  const right = Math.min(r.right + HOLGURA, window.innerWidth - 4);
  return { top, left, width: right - left, height: bottom - top };
};

/** Dónde va la tarjeta: al lado de lo resaltado si es estrecho, debajo o encima, o en una esquina. */
const posicion = (caja: Caja | null, alto: number): React.CSSProperties => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (vw < 640) {
    // Móvil: abajo, encima de la barra de navegación.
    return { left: 12, right: 12, bottom: 76 };
  }
  if (!caja)
    return {
      left: (vw - ANCHO_TARJETA) / 2,
      top: Math.max((vh - alto) / 2, MARGEN),
    };
  const derecha = caja.left + caja.width;
  const abajo = caja.top + caja.height;
  if (caja.width < 300 && derecha + ANCHO_TARJETA + MARGEN * 2 < vw)
    return {
      left: derecha + MARGEN,
      top: Math.min(Math.max(caja.top - 8, MARGEN), vh - alto - MARGEN),
    };
  const left = Math.min(
    Math.max(caja.left, MARGEN),
    vw - ANCHO_TARJETA - MARGEN,
  );
  if (vh - abajo >= alto + MARGEN * 2) return { left, top: abajo + 12 };
  if (caja.top >= alto + MARGEN * 2) return { left, top: caja.top - alto - 12 };
  return { right: MARGEN * 2, bottom: MARGEN * 2 };
};

/**
 * Recorrido guiado (Robert, 02/11/2026): oscurece la pantalla, resalta cada zona y explica qué
 * es en una tarjeta con «Atrás», «Siguiente» y «Saltar». Va de pantalla en pantalla; con el
 * teclado, flechas para moverse y Esc para salir. Mientras está abierto no se puede tocar la app.
 */
export const RecorridoGuiado = ({
  papel,
  pasos,
  alCerrar,
}: {
  papel: PapelGuia;
  pasos: PasoGuia[];
  alCerrar: (terminada: boolean) => void;
}) => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [i, setI] = useState(0);
  const [caja, setCaja] = useState<Caja | null>(null);
  const [listo, setListo] = useState(false);
  const [alto, setAlto] = useState(220);
  const tarjeta = useRef<HTMLDivElement>(null);
  const siguienteRef = useRef<HTMLButtonElement>(null);
  const elemento = useRef<HTMLElement | null>(null);
  const paso = pasos[i];
  const ultimo = i === pasos.length - 1;
  const t = (k: string) => translate(`nevora.guia.${papel}.${paso.id}.${k}`);

  // Ir a la pantalla del paso y esperar a que aparezca lo que hay que resaltar (hasta 2,5 s).
  useEffect(() => {
    setListo(false);
    elemento.current = null;
    setCaja(null);
    if (paso.ruta && paso.ruta !== pathname) navigate(paso.ruta);
    if (!paso.selector) {
      setListo(true);
      return;
    }
    const selector = paso.selector;
    const inicio = Date.now();
    const id = window.setInterval(() => {
      const el = buscar(selector);
      if (el || Date.now() - inicio > 2500) {
        window.clearInterval(id);
        if (el) {
          el.scrollIntoView({ block: "center", inline: "nearest" });
          elemento.current = el;
          setCaja(cajaDe(el));
        }
        setListo(true);
      }
    }, 100);
    return () => window.clearInterval(id);
    // Solo al cambiar de paso (la ruta se cambia aquí mismo).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);

  // Seguir al elemento si la pantalla se mueve o cambia de tamaño.
  useEffect(() => {
    const medir = () => {
      if (elemento.current) setCaja(cajaDe(elemento.current));
    };
    window.addEventListener("resize", medir);
    window.addEventListener("scroll", medir, true);
    return () => {
      window.removeEventListener("resize", medir);
      window.removeEventListener("scroll", medir, true);
    };
  }, []);

  useLayoutEffect(() => {
    if (tarjeta.current) setAlto(tarjeta.current.offsetHeight);
  }, [i, listo]);

  useEffect(() => {
    if (listo) siguienteRef.current?.focus();
  }, [listo, i]);

  const mover = useCallback(
    (paso: 1 | -1) => {
      if (paso === 1 && ultimo) {
        alCerrar(true);
        return;
      }
      setI((n) => Math.min(Math.max(n + paso, 0), pasos.length - 1));
    },
    [alCerrar, pasos.length, ultimo],
  );

  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      if (e.key === "Escape") alCerrar(false);
      else if (e.key === "ArrowRight") mover(1);
      else if (e.key === "ArrowLeft") mover(-1);
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [alCerrar, mover]);

  return createPortal(
    <div className="fixed inset-0 z-[80]" data-testid="guia">
      {/* Capa que no deja tocar la app; con algo resaltado, la oscuridad la pone el recorte. */}
      <div
        className={cn(
          "absolute inset-0",
          !caja && "bg-black/55 transition-colors",
        )}
        aria-hidden
      />
      {caja ? (
        <div
          className="pointer-events-none absolute rounded-xl ring-2 ring-white/80 transition-all duration-300 ease-out"
          style={{
            ...caja,
            boxShadow: "0 0 0 9999px rgba(10, 10, 10, 0.55)",
          }}
          aria-hidden
        />
      ) : null}
      <div
        ref={tarjeta}
        role="dialog"
        aria-modal="true"
        aria-labelledby="guia-titulo"
        aria-describedby="guia-texto"
        className={cn(
          "absolute w-auto rounded-2xl border border-border/60 bg-card p-5 text-card-foreground shadow-elevada transition-opacity duration-200 sm:w-[360px]",
          listo ? "opacity-100" : "opacity-0",
        )}
        style={posicion(caja, alto)}
      >
        <div className="flex items-start justify-between gap-3">
          <p className="etiqueta">
            {translate("nevora.guia.paso", { n: i + 1, total: pasos.length })}
          </p>
          <button
            type="button"
            onClick={() => alCerrar(false)}
            className="-mr-1 -mt-1 rounded-md p-1 text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
            aria-label={translate("nevora.guia.saltar")}
          >
            <X className="size-4" />
          </button>
        </div>
        <h2 id="guia-titulo" className="titulo-seccion mt-1.5 text-lg">
          {t("titulo")}
        </h2>
        <p
          id="guia-texto"
          className="mt-2 whitespace-pre-line text-sm leading-relaxed text-muted-foreground"
        >
          {t("texto")}
        </p>
        {paso.ilustracion ? <Ilustracion tipo={paso.ilustracion} /> : null}
        <div className="mt-4 h-1 overflow-hidden rounded-full bg-foreground/[0.07]">
          <div
            className="h-full rounded-full bg-[var(--serie-llamadas)] transition-all duration-300"
            style={{ width: `${((i + 1) / pasos.length) * 100}%` }}
          />
        </div>
        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => alCerrar(false)}
            className="text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            {translate("nevora.guia.saltar")}
          </button>
          <div className="flex gap-2">
            {i > 0 ? (
              <Button variant="outline" size="sm" onClick={() => mover(-1)}>
                {translate("nevora.guia.atras")}
              </Button>
            ) : null}
            <Button ref={siguienteRef} size="sm" onClick={() => mover(1)}>
              {translate(
                ultimo ? "nevora.guia.terminar" : "nevora.guia.siguiente",
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

/** Los botones de la ficha, dibujados, para que se reconozcan al verlos de verdad. */
const Ilustracion = ({ tipo }: { tipo: "resultados" | "consultoria" }) => {
  const translate = useTranslate();
  const t = (k: string) => translate(`nevora.guia.ilustracion.${k}`);
  const fichas =
    tipo === "resultados"
      ? [
          "quiere_reunion",
          "interesado",
          "ni_si_ni_no",
          "no_contesta",
          "mas_adelante",
          "no_interesa",
        ]
      : ["se_hizo", "no_se_presento", "notas"];
  return (
    <div className="mt-3 space-y-2 rounded-xl bg-foreground/[0.035] p-3">
      {tipo === "resultados" ? (
        <div className="flex flex-wrap gap-1.5">
          {["he_llamado", "he_escrito", "he_visitado"].map((b) => (
            <span
              key={b}
              className={cn(
                "rounded-md border px-2 py-1 text-xs font-medium",
                b === "he_llamado"
                  ? "border-foreground bg-foreground text-background"
                  : "border-border bg-card",
              )}
            >
              {t(b)}
            </span>
          ))}
        </div>
      ) : null}
      <div className="flex flex-wrap gap-1.5">
        {fichas.map((f) => (
          <span
            key={f}
            className="rounded-full border border-border bg-card px-2.5 py-1 text-xs"
          >
            {t(f)}
          </span>
        ))}
      </div>
    </div>
  );
};
